import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/components/ui/select';
import { formatSprintRange } from '../../clientReporting/product/sources/sprintRange';
import { asAmount, formatEur } from './budget';
import type { GridSprint } from './WorkforceGrid';

/** Pays what is held back into one sprint's bill: all of it by default, or less; a release there is undone here too. */
const ReleaseDialog = ({
  into: opensOn,
  held,
  sprints,
  released,
  pendingBefore,
  onRelease,
  onClose,
}: {
  /** The sprint it opens on, or null while closed. */
  into: string | null;
  held: number;
  /** Open sprints only: a closed one's bill has already gone out. */
  sprints: GridSprint[];
  /** What was released into each sprint already. */
  released: Record<string, number>;
  /** Open sprints before the chosen one that will still hold money back, which this release leaves out. */
  pendingBefore: (sprintId: string) => { numbers: number[]; amount: number };
  /** The sprint's released amount as it should now stand; zero undoes it. */
  onRelease: (sprintId: string, amount: number) => void;
  onClose: () => void;
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [into, setInto] = useState(opensOn ?? '');
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (opensOn === null) return;
    setInto(opensOn);
    setTyped(held > 0 ? String(held) : '');
  }, [opensOn, held]);

  const amount = typed.trim() === '' ? null : asAmount(typed);
  const valid = amount !== null && amount > 0 && amount <= held;
  const already = released[into] ?? 0;
  const pending = into ? pendingBefore(into) : { numbers: [], amount: 0 };

  const datesOf = (sprint: GridSprint): string =>
    sprint.startDate && sprint.endDate
      ? formatSprintRange(new Date(sprint.startDate), new Date(sprint.endDate), i18n?.language)
      : '';
  const sprintName = (sprint: GridSprint): string => t('budget.sprintNumber', { number: sprint.number });
  // Two columns: the number's is as wide as "Sprint 26", so the dates line up just after it down the list.
  const sprintLabel = (sprint?: GridSprint): React.ReactNode =>
    sprint ? (
      <span className="grid grid-cols-[4.5rem_auto] items-baseline gap-1 whitespace-nowrap">
        <span>{sprintName(sprint)}</span>
        <span className="text-xs tabular-nums text-muted-foreground">{datesOf(sprint)}</span>
      </span>
    ) : null;
  // Past a few, a list of numbers is noise: the count says as much.
  const leftOut = t(pending.numbers.length > 3 ? 'spend.releasePendingMany' : 'spend.releasePending', {
    count: pending.numbers.length,
    sprints: pending.numbers.join(', '),
    amount: formatEur(pending.amount),
  });

  const release = (): void => {
    if (!valid) return;
    // Added to what went into that sprint already, so a second release adds up.
    onRelease(into, Math.round((already + (amount as number)) * 100) / 100);
    onClose();
  };

  return (
    <Dialog open={opensOn !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('spend.releaseAllTitle')}</DialogTitle>
          <DialogDescription>{t('spend.releaseAllHint', { amount: formatEur(held) })}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={event => {
            event.preventDefault();
            release();
          }}
        >
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t('spend.releaseInto')}</Label>
              <Select
                value={into}
                onValueChange={next => next && setInto(next)}
                items={Object.fromEntries(
                  sprints.map(sprint => [sprint.id, [sprintName(sprint), datesOf(sprint)].filter(Boolean).join(' ')]),
                )}
              >
                <SelectTrigger className="w-60" aria-label={t('spend.releaseInto')}>
                  <SelectValue>
                    {(value: string) => sprintLabel(sprints.find(sprint => sprint.id === value))}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-72 min-w-60" alignItemWithTrigger={false}>
                  {sprints.map(sprint => (
                    <SelectItem key={sprint.id} value={sprint.id}>
                      {sprintLabel(sprint)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="release-amount">{t('spend.releaseAmount')}</Label>
              <span className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">€</span>
                <Input
                  id="release-amount"
                  inputMode="decimal"
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                  aria-invalid={typed.trim() !== '' && !valid}
                  className="w-32 text-right tabular-nums"
                />
              </span>
            </div>
          </div>
          {typed.trim() !== '' && amount !== null && amount > held ? (
            <p className="text-sm text-destructive">{t('spend.overReleased', { amount: formatEur(held) })}</p>
          ) : null}
          {pending.amount > 0 ? (
            <p className="rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">{leftOut}</p>
          ) : null}
          {already > 0 ? (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">
                {t('spend.alreadyReleased', { amount: formatEur(already) })}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => {
                  onRelease(into, 0);
                  onClose();
                }}
              >
                {t('spend.undoRelease')}
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={!valid}>
              {t('spend.releaseConfirm', { amount: formatEur(valid ? (amount as number) : 0) })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseDialog;
